import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, InputBase, ClickAwayListener } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { COLORS } from '../../pages/app/reports/groups-teams-activity/constants';
import { TagOption } from '../../pages/app/reports/groups-teams-activity/types';

type ColorVariant = 'blue' | 'amber';

const COLOR_VARIANTS: Record<ColorVariant, {
    pillBg: string; pillText: string; pillHover: string;
    accent: string; focusRing: string;
}> = {
    blue: {
        pillBg: COLORS.blueLight,
        pillText: COLORS.blueDark,
        pillHover: '#BFDBFE',
        accent: COLORS.primary,
        focusRing: 'rgba(32,111,165,0.15)',
    },
    amber: {
        pillBg: COLORS.amberLight,
        pillText: COLORS.amberDark,
        pillHover: COLORS.amberHover,
        accent: COLORS.amber,
        focusRing: 'rgba(217,119,6,0.15)',
    },
};

interface TagPickerProps {
    options: TagOption[];
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
    minWidth?: number | string;
    colorVariant?: ColorVariant;
    /** Show the count badge next to each tag name. Default true. */
    showCount?: boolean;
}

export const TagPicker: React.FC<TagPickerProps> = ({
    options,
    selected,
    onChange,
    placeholder = 'Select tags…',
    minWidth = 200,
    colorVariant = 'blue',
    showCount = true,
}) => {
    const cv = COLOR_VARIANTS[colorVariant];
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen && searchRef.current) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const filtered = options.filter((opt) =>
        opt.name.toLowerCase().includes(search.toLowerCase())
    );

    const toggleTag = (name: string) => {
        if (selected.includes(name)) {
            onChange(selected.filter((s) => s !== name));
        } else {
            onChange([...selected, name]);
        }
    };

    const removeTag = (name: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(selected.filter((s) => s !== name));
    };

    const clearAll = () => {
        onChange([]);
    };

    return (
        <ClickAwayListener onClickAway={() => { setIsOpen(false); setSearch(''); }}>
            <Box sx={{ position: 'relative' }}>
                {/* Trigger */}
                <Box
                    onClick={() => setIsOpen(!isOpen)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        minWidth,
                        minHeight: '34px',
                        border: `1px solid ${isOpen ? cv.accent : COLORS.border}`,
                        borderRadius: '6px',
                        padding: '4px 28px 4px 8px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        flexWrap: 'wrap',
                        position: 'relative',
                        boxShadow: isOpen ? `0 0 0 2px ${cv.focusRing}` : 'none',
                    }}
                >
                    {selected.length === 0 && (
                        <Typography sx={{ fontSize: '13px', color: COLORS.grayMedium }}>
                            {placeholder}
                        </Typography>
                    )}
                    {selected.map((name) => (
                        <Box
                            key={name}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                padding: '2px 6px 2px 8px',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: cv.pillBg,
                                color: cv.pillText,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {name}
                            <Box
                                onClick={(e) => removeTag(name, e)}
                                sx={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: cv.pillHover },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: '10px' }} />
                            </Box>
                        </Box>
                    ))}
                    <KeyboardArrowDownIcon
                        sx={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '16px',
                            height: '16px',
                            color: COLORS.grayDark,
                        }}
                    />
                </Box>

                {/* Dropdown */}
                {isOpen && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: '4px',
                            backgroundColor: '#fff',
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                            zIndex: 100,
                            maxHeight: '280px',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Search */}
                        <Box sx={{ padding: '8px 12px', borderBottom: `1px solid ${COLORS.border}` }}>
                            <InputBase
                                inputRef={searchRef}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search tags…"
                                sx={{
                                    width: '100%',
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: '5px',
                                    padding: '6px 10px',
                                    fontSize: '12px',
                                    fontFamily: 'Poppins, sans-serif',
                                    '& input::placeholder': { color: '#B0B8C4', opacity: 1 },
                                    '&.Mui-focused': { borderColor: cv.accent },
                                }}
                            />
                        </Box>

                        {/* Options list */}
                        <Box sx={{ overflowY: 'auto', maxHeight: '200px', padding: '4px 0' }}>
                            {filtered.length === 0 ? (
                                <Typography
                                    sx={{
                                        padding: '16px 12px',
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        color: COLORS.grayMedium,
                                    }}
                                >
                                    No tags match your search
                                </Typography>
                            ) : (
                                filtered.map((opt) => {
                                    const isSelected = selected.includes(opt.name);
                                    return (
                                        <Box
                                            key={opt.name}
                                            onClick={() => toggleTag(opt.name)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '6px 12px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                '&:hover': { backgroundColor: COLORS.pageBg },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '4px',
                                                    border: `1.5px solid ${isSelected ? cv.accent : '#C4C9D2'}`,
                                                    backgroundColor: isSelected ? cv.accent : 'transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    transition: 'all .15s',
                                                }}
                                            >
                                                {isSelected && (
                                                    <CheckIcon sx={{ fontSize: '10px', color: '#fff', fontWeight: 700 }} />
                                                )}
                                            </Box>
                                            <Typography sx={{ flex: 1, fontSize: '12px' }}>{opt.name}</Typography>
                                            {showCount && (
                                                <Typography sx={{ fontSize: '10px', color: COLORS.grayMedium }}>
                                                    {opt.count}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })
                            )}
                        </Box>

                        {/* Actions */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                borderTop: `1px solid ${COLORS.border}`,
                                fontSize: '11px',
                            }}
                        >
                            <Typography
                                onClick={clearAll}
                                sx={{
                                    color: COLORS.grayDark,
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    fontSize: '11px',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Clear all
                            </Typography>
                            <Typography
                                onClick={() => { setIsOpen(false); setSearch(''); }}
                                sx={{
                                    color: cv.accent,
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    fontSize: '11px',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Done
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
};
